import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Download,
  FileAudio,
  Loader2,
  Pause,
  Play,
  Share2,
  Volume2,
  VolumeX
} from "lucide-react";

import {
  advanceStep,
  fetchProjectArtifacts,
  fetchScenarioAudio,
  getArtifactAudioUrl,
  getScenarioAudioFileUrl,
  type ArtifactSummary,
} from "@/api/client";
import { useSessionStore } from "@/hooks/useSessionStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";

/* ─── helpers ────────────────────────────────────────────────────────────── */
function formatTime(secs: number) {
  if (!isFinite(secs) || isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatBytes(bytes: number) {
  if (bytes <= 0) return null;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

/* ─── component ──────────────────────────────────────────────────────────── */
export function FinalValidationView() {
  const {
    projectName,
    sessionId,
    setProjectName,
    setSessionId,
    setCurrentStep,
    resetProgress
  } = useSessionStore();
  const navigate = useNavigate();

  /* ── audio state ── */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [audioMeta, setAudioMeta] = useState<{ path?: string; status?: string } | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [audioLoading, setAudioLoading] = useState(true);
  const [audioError, setAudioError] = useState<string | null>(null);

  /* ── artifact selection state ── */
  const [selectedArtifactIds, setSelectedArtifactIds] = useState<Set<string>>(new Set());
  const { data: allArtifacts = [] } = useQuery<ArtifactSummary[]>({
    queryKey: ["project-artifacts", projectName],
    queryFn: () => fetchProjectArtifacts(projectName!),
    enabled: Boolean(projectName),
    staleTime: 30_000,
  });
  // Pre-select current session artifact
  useEffect(() => {
    if (sessionId && !selectedArtifactIds.has(sessionId)) {
      setSelectedArtifactIds((prev) => new Set([...prev, sessionId]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  /* ── dialog / action state ── */
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /* ── resolve audio source ─────────────────────────────────────────────── */
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      setAudioLoading(true);
      setAudioError(null);
      try {
        const meta = await fetchScenarioAudio(sessionId);
        if (cancelled) return;
        setAudioMeta(meta);
        if (meta?.path) {
          // Use the session-scoped /scenario-audio/file endpoint — always has the
          // latest mixed audio produced by the editor, before finalization copies it.
          const url = getScenarioAudioFileUrl(sessionId);
          setAudioSrc(url);
        } else {
          setAudioError("Aucun audio disponible. Retournez à l'étape d'édition.");
        }
      } catch {
        if (!cancelled) setAudioError("Impossible de charger l'audio.");
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  /* ── audio element wiring ─────────────────────────────────────────────── */
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onMeta = () => {
      setDuration(el.duration);
      // Estimate file size from duration × bitrate heuristic (128 kbps MP3)
      if (el.duration && fileSize === null) {
        setFileSize(Math.round(el.duration * 128 * 1000 / 8));
      }
    };
    const onEnd = () => setIsPlaying(false);
    const onErr = () => setAudioError("Erreur de lecture audio. Vérifiez que l'audio a bien été généré.");
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    el.addEventListener("error", onErr);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
      el.removeEventListener("error", onErr);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc]);

  /* ── controls ─────────────────────────────────────────────────────────── */
  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
    } else {
      el.play().then(() => setIsPlaying(true)).catch(() => {
        setAudioError("Impossible de lire l'audio.");
      });
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * duration;
  };

  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !muted;
    setMuted(!muted);
  };

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = audioRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.volume = ratio;
    setVolume(ratio);
    if (muted && ratio > 0) { el.muted = false; setMuted(false); }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  /* ── actions ──────────────────────────────────────────────────────────── */
  const confirm = async () => {
    if (!sessionId) return;
    setIsLoading(true);
    setError(null);
    try {
      await advanceStep(sessionId, "final_validation", { confirmed: true });
      resetProgress();
      setSessionId(undefined);
      setProjectName(undefined);
      setCurrentStep("project_selection");
      setModalOpen(false);
      navigate("/");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleShare = () => {
    const url = audioSrc ?? window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!audioSrc) return;
    const a = document.createElement("a");
    a.href = audioSrc;
    const ext = audioMeta?.path?.endsWith(".mp3") ? "mp3" : "wav";
    a.download = `${projectName ?? "archive"}.${ext}`;
    a.click();
  };

  const handleDownloadArtifact = (artifact: ArtifactSummary) => {
    if (!projectName) return;
    const url = getArtifactAudioUrl(projectName, artifact.session_id);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.name ?? artifact.session_id}.wav`;
    a.click();
  };

  const toggleArtifact = (id: string) => {
    setSelectedArtifactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBatchDownload = () => {
    if (!projectName) return;
    allArtifacts
      .filter((a) => selectedArtifactIds.has(a.session_id) && a.has_audio)
      .forEach((a) => handleDownloadArtifact(a));
  };

  /* ── display values ───────────────────────────────────────────────────── */
  const displayTitle = projectName ? `Projet final "${projectName}"` : "Projet final";
  const audioFormat = audioMeta?.path?.endsWith(".mp3") ? "MP3" : audioMeta?.path?.endsWith(".wav") ? "WAV" : "Audio";
  const sizeLabel = fileSize ? formatBytes(fileSize) : null;

  /* ── render ───────────────────────────────────────────────────────────── */
  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-0 p-6">
      {/* hidden audio element */}
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          preload="metadata"
          key={audioSrc}
        />
      )}

      {/* ── Header ── */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Download className="h-5 w-5 text-[#0F172B]" />
          <h2 className="text-[20px] font-semibold text-[#0F172B]">Exports</h2>
        </div>
        <p className="text-[14px] text-[#45556C]">
          Finaliser et valider l'artefact pour le télécharger.
        </p>
      </div>

      {/* ── Card ── */}
      <div className="w-full rounded-[14px] border border-[#8EA4BD] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.08)] overflow-hidden">

        {/* ── Blue audio player ── */}
        <div className="w-full rounded-t-[14px] bg-[#1D7AFB] px-5 py-4">
          {audioLoading ? (
            <div className="flex items-center gap-3 text-white/80 py-1">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-[14px]">Chargement de l'audio…</span>
            </div>
          ) : audioError ? (
            <div className="flex items-center gap-3 text-white/90 py-1">
              <VolumeX className="h-5 w-5 shrink-0" />
              <span className="text-[14px]">{audioError}</span>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              {/* Play / Pause */}
              <button
                type="button"
                onClick={togglePlay}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30"
                aria-label={isPlaying ? "Pause" : "Lecture"}
              >
                {isPlaying
                  ? <Pause className="h-4 w-4 fill-white" />
                  : <Play className="h-4 w-4 fill-white" />
                }
              </button>

              {/* Scrubber + times */}
              <div className="flex flex-1 flex-col gap-1.5">
                <div
                  className="h-2 w-full cursor-pointer rounded-full bg-white/30"
                  onClick={handleSeek}
                >
                  <div
                    className="h-2 rounded-full bg-white transition-[width]"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-[12px] font-medium text-white/80">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Volume */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleMute}
                  className="text-white/80 hover:text-white transition"
                  aria-label={muted ? "Activer le son" : "Couper le son"}
                >
                  {muted || volume === 0
                    ? <VolumeX className="h-5 w-5" />
                    : <Volume2 className="h-5 w-5" />
                  }
                </button>
                <div
                  className="hidden sm:block h-1.5 w-20 cursor-pointer rounded-full bg-white/30"
                  onClick={handleVolumeClick}
                >
                  <div
                    className="h-1.5 rounded-full bg-white"
                    style={{ width: `${(muted ? 0 : volume) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Info row ── */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {/* Title + action buttons */}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex flex-col gap-0.5">
              <p className="text-[16px] font-semibold text-[#0F172B]">{displayTitle}</p>
              <p className="text-[13px] text-[#45556C]">
                Partager votre travail ou téléchargez le projet.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-[#D0D5DD] bg-white px-4 text-[14px] font-medium text-[#0F172B] transition-colors hover:bg-[#F8FAFC]"
              >
                <Share2 className="h-4 w-4" />
                <span>{copied ? "Copié !" : "Partager"}</span>
              </button>
              <button
                type="button"
                disabled={!audioSrc}
                onClick={() => setModalOpen(true)}
                className="inline-flex h-[38px] items-center gap-1.5 rounded-[10px] bg-[#1D7AFB] px-4 text-[14px] font-medium text-white transition-colors hover:bg-[#1567e0] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                <span>Exporter l'artefact</span>
              </button>
            </div>
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-5 text-[13px] text-[#45556C] border-t border-[#E2E8F0] pt-3 flex-wrap">
            <span>
              <span className="text-[#0F172B]">Format : </span>
              <span className="font-semibold text-[#0F172B]">{audioFormat}</span>
            </span>
            {duration > 0 && (
              <span>
                <span className="text-[#0F172B]">Durée : </span>
                <span className="font-semibold text-[#0F172B]">
                  {Math.floor(duration / 60)} min {Math.round(duration % 60)} s
                </span>
              </span>
            )}
            {sizeLabel && (
              <span>
                <span className="text-[#0F172B]">Taille : </span>
                <span className="font-semibold text-[#0F172B]">{sizeLabel}</span>
              </span>
            )}
          </div>

          {error && (
            <div className="rounded-[8px] border border-[#FF3B30] bg-[#fff1f0] px-4 py-3 text-[13px] text-[#FF3B30]">
              {error}
            </div>
          )}
        </div>
      </div>

      {/* ── Export + archive dialog ── */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Exporter et archiver</DialogTitle>
            <DialogDescription>
              Téléchargez l'audio final et archivez le projet{" "}
              <strong>{projectName}</strong>. Cette action verrouille l'artefact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              disabled={isLoading}
              className="inline-flex h-[38px] items-center gap-1.5 rounded-[10px] border border-[#D0D5DD] bg-white px-4 text-[14px] font-medium text-[#0F172B] transition-colors hover:bg-[#F8FAFC] disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={async () => { handleDownload(); await confirm(); }}
              disabled={isLoading}
              className="inline-flex h-[38px] items-center gap-1.5 rounded-[10px] bg-[#1D7AFB] px-4 text-[14px] font-medium text-white transition-colors hover:bg-[#1567e0] disabled:opacity-50"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              <Download className="h-4 w-4" />
              <span>Télécharger et finaliser</span>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Multi-artifact export panel ── */}
      {allArtifacts.length > 0 && (
        <div className="w-full rounded-[14px] border border-[#E2E8F0] bg-white shadow-[0_2px_10px_rgba(0,0,0,0.05)] overflow-hidden mt-4">
          <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FileAudio className="h-4 w-4 text-[#45556C]" />
              <p className="text-[14px] font-semibold text-[#0F172B]">
                Tous les artefacts du projet
              </p>
              <span className="rounded-full bg-[#EFF6FF] px-2 py-0.5 text-[11px] font-medium text-[#1D7AFB]">
                {allArtifacts.length}
              </span>
            </div>
            <button
              type="button"
              disabled={selectedArtifactIds.size === 0 || !allArtifacts.some((a) => selectedArtifactIds.has(a.session_id) && a.has_audio)}
              onClick={handleBatchDownload}
              className="inline-flex h-[34px] items-center gap-1.5 rounded-[8px] bg-[#1D7AFB] px-3 text-[13px] font-medium text-white transition-colors hover:bg-[#1567e0] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Download className="h-3.5 w-3.5" />
              <span>Télécharger la sélection ({selectedArtifactIds.size})</span>
            </button>
          </div>
          <div className="divide-y divide-[#F1F5F9]">
            {allArtifacts.map((artifact) => {
              const isSelected = selectedArtifactIds.has(artifact.session_id);
              const isCurrent = artifact.session_id === sessionId;
              return (
                <div
                  key={artifact.session_id}
                  className={`flex items-center gap-3 px-5 py-3 transition-colors ${isSelected ? "bg-[#EFF6FF]" : "hover:bg-[#F8FAFC]"}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleArtifact(artifact.session_id)}
                    className="h-4 w-4 accent-[#1D7AFB] cursor-pointer shrink-0"
                    aria-label={`Sélectionner ${artifact.name}`}
                  />
                  <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] font-medium text-[#0F172B] truncate">
                        {artifact.name || artifact.session_id}
                      </span>
                      {isCurrent && (
                        <span className="shrink-0 rounded-full bg-[#1D7AFB] px-2 py-0.5 text-[10px] font-medium text-white">
                          En cours
                        </span>
                      )}
                      {artifact.status === "finalise" && (
                        <span className="shrink-0 rounded-full bg-[#E6F9ED] px-2 py-0.5 text-[10px] font-medium text-[#059669]">
                          Finalisé
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[#94A3B8]">
                      {artifact.created_at && (
                        <span>{new Date(artifact.created_at).toLocaleDateString("fr-FR")}</span>
                      )}
                      {artifact.has_audio && <span>Audio</span>}
                      {artifact.has_video && <span>Vidéo</span>}
                    </div>
                  </div>
                  {artifact.has_audio && (
                    <button
                      type="button"
                      onClick={() => handleDownloadArtifact(artifact)}
                      title="Télécharger cet artefact"
                      className="shrink-0 flex h-8 w-8 items-center justify-center rounded-[8px] border border-[#D0D5DD] bg-white text-[#45556C] transition-colors hover:bg-[#F8FAFC] hover:text-[#1D7AFB]"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
